/**
 * Pure functions that compute the Starred / New this week / Your uploads /
 * All partitions from a full Library item list. Kept outside the page
 * component so the page stays presentation-only and these are trivially
 * unit-testable in isolation.
 *
 * Sections are a landing-page affordance — shown ONLY when the user has no
 * active filter. The moment they type in search or tap a chip, we switch to
 * the flat grid and these sections disappear. So the computations here only
 * run on the unfiltered list.
 */

const MS_PER_DAY = 86400000;

/**
 * Given the full items list + the current user id, return an ordered list of
 * section descriptors. Empty sections are OMITTED — the page component can
 * render a single empty-state row if ALL sections are empty (i.e. a fresh
 * Library), but individual empty sections never take up room.
 *
 * Returns: [{ id, title, items, href }]
 *   id:    stable key for React
 *   title: human-readable heading
 *   items: up to MAX_PER_SECTION cards for the strip
 *   href:  "See all" target (URL search string) when the section is capped;
 *          null when the section shows its full contents.
 */
const MAX_PER_SECTION = 6;

export function computeSections(items, currentUserId) {
  if (!Array.isArray(items) || items.length === 0) return [];

  const now = Date.now();
  const oneWeekAgo = now - 7 * MS_PER_DAY;

  // Starred by me — item.starred_by_me is set by the backend list endpoint.
  const starred = items.filter((it) => it.starred_by_me);

  // New this week — created in the last 7 days.
  const newThisWeek = items.filter((it) => {
    if (!it.created_at) return false;
    const t = new Date(it.created_at).getTime();
    return Number.isFinite(t) && t >= oneWeekAgo;
  });

  // Your uploads — items the current user added.
  const mine = currentUserId
    ? items.filter((it) => it.added_by === currentUserId)
    : [];

  const sections = [];
  if (starred.length) {
    sections.push({
      id: "starred",
      title: "Starred",
      items: starred.slice(0, MAX_PER_SECTION),
      count: starred.length,
      href: starred.length > MAX_PER_SECTION ? "?starred=1" : null,
    });
  }
  if (newThisWeek.length) {
    sections.push({
      id: "new-this-week",
      title: "New this week",
      items: newThisWeek.slice(0, MAX_PER_SECTION),
      count: newThisWeek.length,
      // No filter URL for time-window sections yet — show everything inline
      // if under cap, otherwise link into the grid without a filter.
      href: newThisWeek.length > MAX_PER_SECTION ? "?sort=recent" : null,
    });
  }
  if (mine.length) {
    sections.push({
      id: "mine",
      title: "Your uploads",
      items: mine.slice(0, MAX_PER_SECTION),
      count: mine.length,
      href: mine.length > MAX_PER_SECTION ? `?uploader=${encodeURIComponent(currentUserId)}` : null,
    });
  }

  return sections;
}

/**
 * Compute the top N tags from the tag cloud response, with an overflow count
 * for the "+N more" affordance in the chip row.
 *
 * Input: [{tag, count}] (server-sorted desc)
 * Output: { visible: [{tag, count}], overflow: [{tag, count}], total: int }
 */
export function splitTagChips(tagCloud, visibleLimit = 10) {
  if (!Array.isArray(tagCloud) || tagCloud.length === 0) {
    return { visible: [], overflow: [], total: 0 };
  }
  return {
    visible: tagCloud.slice(0, visibleLimit),
    overflow: tagCloud.slice(visibleLimit),
    total: tagCloud.length,
  };
}
